/**
 * Sold-out: still in stock (sections/sold-out-alternatives.liquid) and the data helpers shared by the rows under the buy
 * box: the scoped recommendation rows (<so-scope-row>, sections/product-recommendations.liquid), Recently viewed
 * (assets/recently-viewed-row.js) and Other printings (assets/printings-ladder.js) import them with a dynamic import().
 *
 * How products are found: the Section Rendering API calls sections/sold-out-alternatives.liquid by name, on the storefront
 * search page, for example /search?q=title:"OP05-067"&type=product&section_id=sold-out-alternatives&filter.v.availability=1,
 * or on a collection page (/collections/one-piece-accessories?filter.v.availability=1&section_id=sold-out-alternatives), and
 * gets a compact JSON record per product (see the header of that file). Everything is filtered again here, in the browser, so
 * a row only shows products that really are the same card / name / set.
 *
 * How often: nothing is requested when the page loads. Every row waits until it is within about 600px of the viewport
 * (whenNear), and all rows go through ONE request layer: a window-level Map keyed by the full url, so identical requests are
 * made once per page. The rows are merged on purpose, so a card page needs at most two data requests in total:
 *   - the "card" request: the same card in every printing and condition (Other printings, the sold-out "other printings"
 *     row). The ids of the recently viewed products ride along in the same query, so Recently viewed needs none of its own.
 *   - the "group" request: the in-stock products of the same set AND of the same character / name in one query
 *     (vendor:"X" OR title:"Zoro"), for More from this set, Same character and the sold-out name / set rows.
 *   - accessories: one request to the game's accessories collection (in-stock products only) instead of a search.
 * A failed request (429, 5xx, network, timeout) is retried once after about 4 seconds and is never remembered: after the
 * second failure the row simply stays hidden (no error is thrown, nothing is logged).
 */

const REQUEST_TIMEOUT_MS = 9000;
const RETRY_DELAY_MS = 4000;
const LAZY_MARGIN = '600px 0px';
const DEFAULT_SOURCE = 'sold-out-alternatives';
const GRADER_VENDORS = ['psa', 'bgs', 'cgc', 'sgc', 'tag', 'ace', 'beckett', 'pca', 'mnt'];
/** Recently viewed ids that ride along with the card request (the history holds 12 products, one of them this page). */
const VIEWED_CARRY = 11;

/**
 * @typedef {object} Item One product record from the data endpoint.
 * @property {number} id
 * @property {string} t title
 * @property {string} u path to the product page
 * @property {string} i image url ('' when there is none)
 * @property {number} a 1 when available
 * @property {string} s state: available | soldout | upcoming
 * @property {string} k kind: single | sealed | graded | accessory | other
 * @property {string} g game: pokemon | onepiece | other
 * @property {string} l language: english | japanese | chinese
 * @property {string} v vendor (the set for cards and sealed product)
 * @property {string} n card number or code
 * @property {string} p printing (Foil, Holofoil, ...)
 * @property {string} r rarity (Rare, Illustration Rare, ...)
 * @property {number} pm cheapest available price in cents
 * @property {number} pf 1 when the available prices differ
 * @property {string} pt formatted price
 */

/**
 * @typedef {object} Data One answer of the data endpoint.
 * @property {Item[]} items
 * @property {boolean} complete false when the search had more results than the 50 the endpoint lists
 */

/**
 * @typedef {object} Me The identity of the product on the page.
 * @property {string} id
 * @property {string} title
 * @property {string} game
 * @property {string} kind
 * @property {string} lang
 * @property {string} vendor
 * @property {string} number
 * @property {string} name card name ("Charizard ex")
 * @property {string} base character name without ex / V / Mega ("Charizard")
 * @property {string} code One Piece card code ("OP05-067") or ''
 * @property {string[]} nums Pokemon number tokens (["170", "165"]) or []
 */

/* ------------------------------------------------------------------ text helpers */

/** Lower case, no accents, every run of other characters becomes one space. @param {unknown} text */
export function normalize(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const GRADE_PREFIX =
  /^\s*(?:PSA|BGS|CGC|SGC|TAG|ACE|BECKETT|PCA|MNT)\s+\d+(?:\.\d+)?(?:\s+(?:Pristine|Gem\s+Mint|Perfect|Black\s+Label|Mint))?\s+/i;

/** "PSA 10 Boa Hancock (ST17-004) ..." becomes "Boa Hancock (ST17-004) ...". @param {unknown} title */
export function stripGrade(title) {
  return String(title ?? '').replace(GRADE_PREFIX, '');
}

/**
 * The card (or product) name at the start of a title: everything before " - ", " (" or "[", without a trailing number.
 * "Charizard ex 161  - Holofoil ..." gives "Charizard ex"; "Zoro-Juurou (SP) (OP05-067) - ..." gives "Zoro-Juurou";
 * "Triple Whammy Tin [Tyranitar]" gives "Triple Whammy Tin".
 * @param {unknown} title
 */
export function cardName(title) {
  const first = stripGrade(title).split(/\s+[-\u2013\u2014]\s+|\s+\(|\s*\[/)[0] || '';
  return first.replace(/\s+[A-Za-z]{0,5}\d[\w/.-]*$/, '').trim();
}

const NAME_SUFFIX = /\s+(?:ex|gx|v|vmax|vstar|v-union|break|prime|star|lv\.?\s?x)$/i;
const NAME_PREFIX = /^(?:mega|dark|light|shining|radiant|alolan|galarian|hisuian|paldean|primal|shadow)\s+/i;
const TEAM_PREFIX = /^team\s+[a-z]+'s\s+/i;

/**
 * The character behind a card name: "Charizard ex" and "Mega Charizard X ex" both give "Charizard".
 * One Piece names are used as they are. DON!! cards have no character.
 * @param {string} name
 * @param {string} game
 */
export function characterBase(name, game) {
  let base = String(name || '').trim();
  if (!base || /^don!!/i.test(base)) return '';
  if (game === 'pokemon') {
    for (let i = 0; i < 3 && NAME_SUFFIX.test(base); i += 1) base = base.replace(NAME_SUFFIX, '');
    base = base.replace(TEAM_PREFIX, '');
    if (NAME_PREFIX.test(base)) base = base.replace(NAME_PREFIX, '').replace(/\s+[xy]$/i, '');
  }
  base = base.trim();
  return base.length >= 3 ? base : '';
}

const OP_CODE_PAREN = /\(([A-Z]{1,4}\d{0,2}-\d{2,3})\)/;
const OP_CODE_BARE = /(?:^|[^A-Za-z0-9])([A-Z]{1,4}\d{0,2}-\d{2,3})(?![A-Za-z0-9])/;
const OP_CODE_ONLY = /^[A-Za-z]{1,4}\d{0,2}-\d{2,3}$/;

/** The One Piece card code in a title: "Zoro-Juurou (SP) (OP05-067) - ..." gives "OP05-067", else ''. @param {unknown} title */
export function opCodeFromTitle(title) {
  const text = String(title ?? '');
  const match = text.match(OP_CODE_PAREN) || text.match(OP_CODE_BARE);
  return match ? match[1].toUpperCase() : '';
}

/** @param {string} token */
function normToken(token) {
  return /^\d+$/.test(token) ? String(parseInt(token, 10)) : token;
}

const NUMBER_PAIR = /(?:^|[^\d])(\d{1,4})\s*\/\s*(\d{1,4})(?!\d)/;

/**
 * The tokens that identify a Pokemon card number. "170/165" gives ["170", "165"]. The product's own number (custom.card_code
 * or the Number_ tag) wins; then a "170/165" pair in the title; a bare number such as 161 (or "SVP161") gives one token.
 * @param {unknown} title
 * @param {unknown} number
 */
export function numberTokens(title, number) {
  const fromNumber = String(number ?? '').match(NUMBER_PAIR);
  if (fromNumber) return [normToken(fromNumber[1] ?? ''), normToken(fromNumber[2] ?? '')];
  const fromTitle = String(title ?? '').match(NUMBER_PAIR);
  if (fromTitle) return [normToken(fromTitle[1] ?? ''), normToken(fromTitle[2] ?? '')];
  return String(number ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map(normToken);
}

/**
 * @param {{ id?: string, title?: string, game?: string, kind?: string, lang?: string, vendor?: string, number?: string }} raw
 * @returns {Me}
 */
export function identityOf(raw) {
  const title = String(raw.title || '');
  const game = raw.game || 'other';
  const number = String(raw.number || '').trim();
  const name = cardName(title);
  let code = '';
  if (game === 'onepiece') {
    code = OP_CODE_ONLY.test(number) ? number.toUpperCase() : opCodeFromTitle(title);
  }
  return {
    id: String(raw.id || ''),
    title,
    game,
    kind: raw.kind || 'other',
    lang: raw.lang || 'english',
    vendor: String(raw.vendor || ''),
    number,
    name,
    base: characterBase(name, game),
    code,
    nums: game === 'pokemon' ? numberTokens(title, number) : [],
  };
}

/** Does the title contain the code as a whole word? "OP05-067" is not found in "XOP05-0678". @param {unknown} title @param {string} code */
function hasCode(title, code) {
  const text = String(title || '').toLowerCase();
  const needle = code.toLowerCase();
  let at = text.indexOf(needle);
  while (at !== -1) {
    const before = text.charAt(at - 1);
    const after = text.charAt(at + needle.length);
    const clearBefore = !before || !/[a-z0-9]/.test(before);
    const clearAfter = !after || !/[a-z0-9]/.test(after);
    if (clearBefore && clearAfter) return true;
    at = text.indexOf(needle, at + 1);
  }
  return false;
}

/**
 * Is this product the same card as the one on the page (another printing or condition)?
 * One Piece: the title contains the same code (or the same number tag). Pokemon: the title contains the same name AND
 * the same number (every number token).
 * @param {Me} me
 * @param {Item} item
 */
export function sameCard(me, item) {
  if (me.game === 'onepiece') {
    if (!me.code) return false;
    if (String(item.n || '').toUpperCase() === me.code) return true;
    return hasCode(item.t, me.code);
  }
  if (me.game === 'pokemon') {
    if (!me.name || !me.nums.length) return false;
    const title = normalize(item.t);
    if (!` ${title} `.includes(` ${normalize(me.name)} `)) return false;
    const have = new Set(title.split(' ').map(normToken));
    for (const token of String(item.n || '').toLowerCase().split(/[^a-z0-9]+/)) {
      if (token) have.add(normToken(token));
    }
    if (!me.nums.every((token) => have.has(token))) return false;
    // A bare number ("161", no "/165" total) exists in many sets, so then the set has to be the same as well.
    if (me.nums.length < 2 && usableSet(me.vendor)) return item.v === me.vendor;
    return true;
  }
  return false;
}

/** Same card name (another card or product with exactly this name). @param {Me} me @param {Item} item */
export function sameName(me, item) {
  return Boolean(me.name) && normalize(cardName(item.t)) === normalize(me.name);
}

/** Which kinds of products may be offered for a product of this kind. @param {string} kind */
export function kindsFor(kind) {
  if (kind === 'single') return ['single'];
  if (kind === 'graded') return ['single', 'graded'];
  if (kind === 'sealed') return ['sealed'];
  if (kind === 'accessory') return ['accessory'];
  return [];
}

/** A value that is safe inside a double-quoted search term. @param {unknown} value */
export function quoted(value) {
  return String(value ?? '')
    .replace(/["\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A vendor that really is a set (not a grader, Bandai / Bandai Asia or the catch-all). @param {string} vendor */
export function usableSet(vendor) {
  const text = String(vendor || '').trim().toLowerCase();
  return Boolean(text) && !/miscellaneous/.test(text) && !/^bandai\b/.test(text) && !GRADER_VENDORS.includes(text);
}

/** A number that is the same for the same product on every page view. @param {unknown} id */
export function seedOf(id) {
  return parseInt(String(id ?? '').slice(-6), 10) || 0;
}

/** Starts the list at a different place for every product, so rows are not identical on every page. */
export function rotate(/** @type {Item[]} */ items, /** @type {number} */ seed) {
  if (items.length < 2) return items;
  const start = Math.abs(seed) % items.length;
  return items.slice(start).concat(items.slice(0, start));
}

/**
 * Same language first, then the same set; otherwise the order is kept (the sort is stable).
 * @param {Me} me
 * @param {Item[]} items
 */
export function rank(me, items) {
  const score = (/** @type {Item} */ item) => (item.l === me.lang ? 2 : 0) + (me.vendor && item.v === me.vendor ? 1 : 0);
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => score(b.item) - score(a.item) || a.index - b.index)
    .map((entry) => entry.item);
}

/* ------------------------------------------------------------------ lazy start */

/**
 * The nearest ancestor that scrolls vertically inside the page (this theme scrolls .page-wrapper, not the window), or null.
 * An IntersectionObserver only applies its rootMargin to its root, so it has to be told which element scrolls.
 * @param {Element} node
 * @returns {Element | null}
 */
function scrollParent(node) {
  for (let parent = node.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
    const overflow = getComputedStyle(parent).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') && parent.scrollHeight > parent.clientHeight + 1) return parent;
  }
  return null;
}

/**
 * Runs `start` once, when the row comes within about 600px of the viewport (an IntersectionObserver with a rootMargin).
 * The rows are rendered hidden until they have something to show, and a hidden element is never "visible" to an
 * IntersectionObserver, so a 1px anchor (zero height in the layout) is put just before the hidden wrapper and observed
 * instead. Without IntersectionObserver (or with nothing that can be observed) the row starts when the browser is idle.
 * @param {HTMLElement} element
 * @param {() => void} start
 * @returns {() => void} cancels the wait
 */
export function whenNear(element, start) {
  let done = false;
  /** @type {IntersectionObserver | null} */
  let observer = null;
  /** @type {HTMLElement | null} */
  let anchor = null;

  /** @type {HTMLElement | null} */
  let hiddenRoot = null;
  for (let node = /** @type {HTMLElement | null} */ (element); node && node !== document.body; node = node.parentElement) {
    if (node.hidden) hiddenRoot = node;
  }
  let target = element;
  if (hiddenRoot) {
    anchor = document.createElement('div');
    anchor.setAttribute('aria-hidden', 'true');
    anchor.setAttribute('data-lazy-anchor', '');
    anchor.style.cssText = 'display:block;height:1px;margin:0 0 -1px;pointer-events:none';
    hiddenRoot.before(anchor);
    target = anchor;
  }

  const cleanup = () => {
    observer?.disconnect();
    observer = null;
    anchor?.remove();
    anchor = null;
  };
  const go = () => {
    if (done) return;
    done = true;
    cleanup();
    try {
      start();
    } catch (_) {
      // The row is optional: it stays hidden.
    }
  };

  if ('IntersectionObserver' in window && target.getClientRects().length > 0) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) go();
      },
      { root: scrollParent(target), rootMargin: LAZY_MARGIN }
    );
    observer.observe(target);
  } else if ('requestIdleCallback' in window) {
    window.requestIdleCallback(go, { timeout: 2500 });
  } else {
    window.setTimeout(go, 1500);
  }

  return () => {
    done = true;
    cleanup();
  };
}

/* ------------------------------------------------------------------ requests */

/**
 * @typedef {object} Shared What every row on the page shares. It lives on window, so a second copy of this file (another
 * asset url) uses the same requests.
 * @property {Map<string, Promise<Data | null>>} requests the requests made so far, by full url (a failed one is removed)
 * @property {Map<string, Item>} items every product record received so far, by id
 * @property {Set<string>} covered ids that were asked for in a complete answer (a product that is not there is gone)
 */

/** @returns {Shared} */
function shared() {
  const root = /** @type {any} */ (window);
  if (!root.__ocgRows || !(root.__ocgRows.requests instanceof Map)) {
    root.__ocgRows = { requests: new Map(), items: new Map(), covered: new Set() };
  }
  return root.__ocgRows;
}

/** @param {unknown} value @returns {value is Item} */
function isItem(value) {
  const record = /** @type {any} */ (value);
  return Boolean(record) && typeof record === 'object' && typeof record.id === 'number' && typeof record.t === 'string';
}

/**
 * One try. `retry` is true for what is worth a second try: throttled (429), server errors (5xx), network errors, timeouts.
 * @param {string} url
 * @returns {Promise<{ data?: Data, retry?: boolean }>}
 */
async function attempt(url) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
    if (response.status === 429 || response.status >= 500) return { retry: true };
    if (!response.ok) return {};

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = doc.querySelector('script[data-so-data]');
    if (!node) return {};
    const list = JSON.parse(node.textContent || '[]');
    if (!Array.isArray(list)) return {};

    let total = list.length >= 50 ? Number.POSITIVE_INFINITY : list.length;
    try {
      const meta = JSON.parse(doc.querySelector('script[data-so-meta]')?.textContent || '{}');
      if (Number.isFinite(meta.total)) total = meta.total;
    } catch (_) {
      // No meta: the length decides.
    }
    return { data: { items: list.filter(isItem), complete: total <= list.length } };
  } catch (_) {
    return { retry: true };
  } finally {
    window.clearTimeout(timer);
  }
}

/** @param {number} ms */
function pause(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Asks for the url, and once more after about 4 seconds when the first try was throttled or failed. Never rejects. @param {string} url @returns {Promise<Data | null>} */
async function load(url) {
  let result = await attempt(url);
  if (!result.data && result.retry) {
    await pause(RETRY_DELAY_MS + Math.floor(Math.random() * 500));
    result = await attempt(url);
  }
  return result.data ?? null;
}

/**
 * The one door to the network: identical urls share one promise. A failed request is forgotten at once, so it is not stuck
 * for the rest of the page view (a later row may ask again); the rows that waited for it simply stay hidden.
 * @param {string} url
 * @returns {Promise<Data | null>}
 */
function request(url) {
  const state = shared();
  let pending = state.requests.get(url);
  if (!pending) {
    pending = load(url).then((data) => {
      if (data) {
        for (const item of data.items) state.items.set(String(item.id), item);
      } else {
        state.requests.delete(url);
      }
      return data;
    });
    state.requests.set(url, pending);
  }
  return pending;
}

/** @returns {string} */
function root() {
  return /** @type {any} */ (window).Shopify?.routes?.root ?? '/';
}

/**
 * Searches the store (max 50 results, in search order) through the data endpoint.
 * @param {string} source section file name of the data endpoint
 * @param {string} query storefront search terms, e.g. title:"OP05-067" or title:"Squirtle" AND title:170 or vendor:"SV: Scarlet & Violet 151"
 * @param {{ available?: boolean }} [options] available: ask the search for in-stock products only
 * @returns {Promise<Data | null>} null when the request failed
 */
export function searchData(source, query, { available = false } = {}) {
  const params = new URLSearchParams({ q: query, type: 'product', section_id: source || DEFAULT_SOURCE });
  if (available) params.set('filter.v.availability', '1');
  return request(`${root()}search?${params}`);
}

/** Same as searchData, but only the products (an empty list when the request failed). @param {string} source @param {string} query @param {{ available?: boolean }} [options] @returns {Promise<Item[]>} */
export function searchProducts(source, query, options) {
  return searchData(source, query, options).then((data) => (data ? data.items : []));
}

/**
 * The in-stock products of a collection (up to 50), through the data endpoint on the collection page.
 * @param {string} source
 * @param {string} handle collection handle
 * @returns {Promise<Data | null>}
 */
export function collectionData(source, handle) {
  const params = new URLSearchParams({ 'filter.v.availability': '1', section_id: source || DEFAULT_SOURCE });
  return request(`${root()}collections/${encodeURIComponent(handle)}?${params}`);
}

/* ------------------------------------------------------------------ cards */

/** Shopify image urls take a width parameter. @param {string} url @param {number} width */
export function sizedUrl(url, width) {
  if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/** Only same-site paths become links. @param {unknown} path */
export function safePath(path) {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : '#';
}

/** The product handle in a product path ("/products/zoro" and "/collections/x/products/zoro?_pos=1" give "zoro"), else ''. @param {unknown} path */
export function handleOf(path) {
  const match = String(path ?? '').match(/\/products\/([^/?#]+)/);
  return match?.[1] ?? '';
}

/** "In stock", "Sold out" or "Coming soon" as text, never colour alone. @param {Item} item */
export function stockLabel(item) {
  if (item.s === 'upcoming') return { text: 'Coming soon', modifier: 'soon' };
  return item.a === 1 ? { text: 'In stock', modifier: '' } : { text: 'Sold out', modifier: 'out' };
}

/** The price as shown on cards: "From $12.00 CAD" when the available prices differ. @param {Item} item */
export function priceText(item) {
  if (!(item.pm > 0)) return '';
  return `${item.pf === 1 ? 'From ' : ''}${item.pt || ''}`.trim();
}

/**
 * The product card used by every row (same markup as the server-rendered fallback cards).
 * @param {Item} item
 * @returns {HTMLLIElement}
 */
export function buildCard(item) {
  const li = document.createElement('li');
  const link = document.createElement('a');
  link.className = 'so-card';
  link.href = safePath(item.u);

  const media = document.createElement('span');
  media.className = 'so-card__media';
  if (item.i) {
    const img = document.createElement('img');
    img.className = 'so-card__img';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = sizedUrl(item.i, 400);
    img.srcset = [200, 300, 400].map((width) => `${sizedUrl(item.i, width)} ${width}w`).join(', ');
    img.sizes = '(min-width: 750px) 180px, 44vw';
    media.append(img);
  }

  const title = document.createElement('span');
  title.className = 'so-card__title';
  title.textContent = item.t;

  const label = stockLabel(item);
  const stock = document.createElement('span');
  stock.className = `so-card__stock${label.modifier ? ` so-card__stock--${label.modifier}` : ''}`;
  stock.textContent = label.text;

  link.append(media, title);
  const cost = priceText(item);
  if (cost) {
    const price = document.createElement('span');
    price.className = 'so-card__price';
    price.textContent = cost;
    link.append(price);
  }
  link.append(stock);
  li.append(link);
  return li;
}

/* ------------------------------------------------------------------ the requests of the rows */

/**
 * The searches that find the same card: One Piece searches the card code in the title (title:"OP05-067"); Pokemon searches
 * the name AND each number token in the title field (title:"Squirtle" AND title:170 AND title:165). The second query is the
 * same words without the field, used only when the first one does not even find the product itself.
 * Empty when the product is not a single or a slab, or the card has no usable code / number.
 * @param {Me} me
 * @returns {string[]}
 */
export function cardQueries(me) {
  if (me.kind !== 'single' && me.kind !== 'graded') return [];
  const name = quoted(me.name);
  if (me.game === 'onepiece' && me.code) return [`title:"${me.code}"`, me.code];
  if (me.game === 'pokemon' && name && me.nums.length) {
    return [`title:"${name}"${me.nums.map((token) => ` AND title:${token}`).join('')}`, `${name} ${me.nums.join(' ')}`];
  }
  return [];
}

/**
 * The recently viewed product ids, newest first, without this product: the same keys as assets/recently-viewed-products.js.
 * Every row computes the same list, so the card request has the same url for all of them.
 * @param {string} [excludeId]
 * @returns {string[]}
 */
export function viewedIds(excludeId = '') {
  /** @param {string} key @returns {string[]} */
  const read = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch (_) {
      return [];
    }
  };
  const history = read('viewedProductsHistory');
  for (const id of read('viewedProducts')) {
    if (!history.includes(id)) history.push(id);
  }
  return history
    .slice(0, 12)
    .filter((id) => /^\d+$/.test(id) && id !== String(excludeId))
    .slice(0, VIEWED_CARRY);
}

/** @param {string} query @param {string[]} ids */
function withIds(query, ids) {
  return ids.length ? `(${query}) OR ${ids.map((id) => `id:${id}`).join(' OR ')}` : query;
}

/**
 * The card request: every product that is the same card (all printings, sold out ones too), plus the recently viewed
 * products by id. Shared by Other printings, the sold-out "other printings" row and Recently viewed.
 * @param {string} source
 * @param {Me} me
 * @returns {Promise<Data | null>} null when the card has no usable code / number or the request failed
 */
export async function cardData(source, me) {
  const queries = cardQueries(me);
  const primary = queries[0];
  if (!primary) return null;

  const ids = viewedIds(me.id);
  const data = await searchData(source, withIds(primary, ids));
  if (!data) return null;
  if (data.complete) for (const id of ids) shared().covered.add(id);

  // The title query finds the product itself, or it does not work for this one: then try the plain words once.
  const found = data.items.some((item) => String(item.id) === me.id);
  const plain = queries[1];
  if (found || !plain) return data;
  return (await searchData(source, plain)) ?? data;
}

/**
 * The group request, in-stock products only: the same set and the same character (or card name) in ONE query.
 * "More from this set" and "Same character" read it on an available product page; the sold-out row (alternatives) reads it
 * for the same name and the same set. The query only depends on the product, so all rows on a page ask the same url.
 * @param {Me} me
 * @param {boolean} [alternatives] the sold-out row is also looking for the same name (sealed and accessory products)
 * @returns {string} '' when there is nothing to ask
 */
export function groupQuery(me, alternatives = false) {
  /** @type {string[]} */
  const terms = [];
  if ((me.kind === 'single' || me.kind === 'sealed') && usableSet(me.vendor)) terms.push(`vendor:"${quoted(me.vendor)}"`);

  const name = quoted(me.name);
  const base = quoted(me.base);
  if (me.kind === 'single' || me.kind === 'graded') {
    const word = base.length >= 3 ? base : alternatives ? name : '';
    if (word.length >= 3) terms.push(`title:"${word}"`);
  } else if (alternatives && (me.kind === 'sealed' || me.kind === 'accessory') && name.length >= 3) {
    terms.push(`title:"${name}"`);
  }
  return terms.join(' OR ');
}

/** @param {string} source @param {Me} me @param {boolean} [alternatives] @returns {Promise<Data | null>} */
export function groupData(source, me, alternatives = false) {
  const query = groupQuery(me, alternatives);
  return query ? searchData(source, query, { available: true }) : Promise.resolve(null);
}

/**
 * The in-stock products of the group request that pass `keep`. When the group request was cut off at 50 results and too
 * few products passed, the row's own precise query is asked instead (rare: only for very large sets and popular characters).
 * @param {string} source
 * @param {Me} me
 * @param {{ keep: (item: Item) => boolean, own: string, need: number, alternatives?: boolean }} options
 * @returns {Promise<Item[]>}
 */
export async function groupItems(source, me, { keep, own, need, alternatives = false }) {
  const data = await groupData(source, me, alternatives);
  if (!data) return [];
  let kept = data.items.filter(keep);
  if (!data.complete && kept.length < need && own && own !== groupQuery(me, alternatives)) {
    const exact = await searchData(source, own, { available: true });
    if (exact) kept = exact.items.filter(keep);
  }
  return kept;
}

/**
 * The records of the recently viewed products. They ride along with the card request when there is one; what is still
 * missing is asked for by id, in one request.
 * @param {string} source
 * @param {Me | null} me the product on the page, when there is one
 * @param {string[]} ids
 * @returns {Promise<Map<string, Item>>} by id (a product that no longer exists is not in it)
 */
export async function viewedItems(source, me, ids) {
  /** @type {Map<string, Item>} */
  const found = new Map();
  if (!ids.length) return found;

  const state = shared();
  let carried = true;
  if (me && cardQueries(me).length) carried = (await cardData(source, me)) !== null;

  // When the card request failed (throttled, offline) the row stays hidden: no second request is made behind it.
  const missing = carried ? ids.filter((id) => !state.covered.has(id) && !state.items.has(id)) : [];
  if (missing.length) {
    const data = await searchData(source, missing.map((id) => `id:${id}`).join(' OR '));
    if (data?.complete) for (const id of missing) state.covered.add(id);
  }

  for (const id of ids) {
    const item = state.items.get(id);
    if (item) found.set(id, item);
  }
  return found;
}

/** @param {DOMStringMap} ds */
export function meFrom(ds) {
  return identityOf({ id: ds.id, title: ds.title, game: ds.game, kind: ds.kind, lang: ds.lang, vendor: ds.vendor, number: ds.number });
}

/** Shows a wrapper that the server rendered hidden. @param {Element | null} wrap */
function reveal(wrap) {
  if (wrap instanceof HTMLElement) wrap.hidden = false;
}

/* ------------------------------------------------------------------ sold-out alternatives row */

class SoAlternatives extends HTMLElement {
  /** @type {(() => void) | null} */
  #cancel = null;

  connectedCallback() {
    if (this.hasAttribute('data-started')) return;
    this.setAttribute('data-started', '');
    this.#cancel = whenNear(this, () => {
      this.#run().catch(() => {});
    });
  }

  disconnectedCallback() {
    this.#cancel?.();
  }

  async #run() {
    const ds = this.dataset;
    const me = meFrom(ds);
    const kinds = kindsFor(me.kind);
    if (!kinds.length) return;

    const source = ds.source || DEFAULT_SOURCE;
    const count = Math.max(1, parseInt(ds.count || '6', 10) || 6);
    const base = (/** @type {Item} */ item) =>
      String(item.id) !== me.id && item.a === 1 && (me.game === 'other' || item.g === me.game) && kinds.includes(item.k);

    if (me.kind === 'accessory') {
      await this.#accessories(source, me, count, base);
      return;
    }

    // 1. The same card: the request is shared with Other printings (and carries the recently viewed ids).
    if (me.kind === 'single' || me.kind === 'graded') {
      const data = await cardData(source, me);
      const kept = data ? data.items.filter((item) => base(item) && sameCard(me, item)) : [];
      if (kept.length) {
        this.#show('card', rank(me, kept).slice(0, count), me);
        return;
      }
    }

    // 2. The same card name and 3. the same set are answered by ONE request.
    const name = quoted(me.name);
    if (name.length >= 3) {
      const kept = await groupItems(source, me, {
        keep: (item) => base(item) && sameName(me, item),
        own: `title:"${name}"`,
        need: 1,
        alternatives: true,
      });
      if (kept.length) {
        this.#show('name', rank(me, kept).slice(0, count), me);
        return;
      }
    }

    if ((me.kind === 'single' || me.kind === 'sealed') && usableSet(me.vendor)) {
      const kept = await groupItems(source, me, {
        keep: (item) => base(item) && item.v === me.vendor,
        own: `vendor:"${quoted(me.vendor)}"`,
        need: 1,
        alternatives: true,
      });
      if (kept.length) this.#show('set', rank(me, rotate(kept, seedOf(me.id))).slice(0, count), me);
    }
  }

  /**
   * Accessories: ONE request to the game's accessories collection (in-stock products only). The same name goes in the first
   * row, the rest of the in-stock accessories replace the server-rendered "still in stock" row (the server only sees the
   * first 50 products of a collection, so it can miss in-stock ones). Without a collection (game unknown) it is a name search.
   * @param {string} source
   * @param {Me} me
   * @param {number} count
   * @param {(item: Item) => boolean} base
   */
  async #accessories(source, me, count, base) {
    const handle = this.dataset.accessoryCollection || '';
    if (!handle) {
      const name = quoted(me.name);
      if (name.length < 3) return;
      const kept = await groupItems(source, me, {
        keep: (item) => base(item) && sameName(me, item),
        own: `title:"${name}"`,
        need: 1,
        alternatives: true,
      });
      if (kept.length) this.#show('name', rank(me, kept).slice(0, count), me);
      return;
    }

    const data = await collectionData(source, handle);
    if (!data) return;

    const pool = data.items.filter(base);
    const named = rank(me, pool.filter((item) => sameName(me, item))).slice(0, count);
    const rest = pool.filter((item) => !named.includes(item));
    this.#fillFallback(rank(me, rotate(rest, seedOf(me.id))).slice(0, count));
    if (named.length) this.#show('name', named, me);
    else this.#reconcile([]);
  }

  /** Replaces the cards of the "still in stock" row; hides the row when there are none. @param {Item[]} items */
  #fillFallback(items) {
    const fallback = this.querySelector('[data-fallback]');
    const list = this.querySelector('[data-fallback-list]');
    if (!(fallback instanceof HTMLElement) || !list) return;
    if (!items.length) {
      fallback.hidden = true;
      return;
    }
    list.replaceChildren(...items.map(buildCard));
    fallback.hidden = false;
    reveal(this.closest('.so-alt-wrap'));
  }

  /** @param {'card' | 'name' | 'set'} key @param {Item[]} items @param {Me} me */
  #show(key, items, me) {
    const found = this.querySelector('[data-found]');
    const list = this.querySelector('[data-found-list]');
    const heading = this.querySelector('[data-found-heading]');
    if (!(found instanceof HTMLElement) || !list || !heading) return;

    const ds = this.dataset;
    const template = key === 'card' ? ds.headingCode : key === 'name' ? ds.headingName : ds.headingSet;
    heading.textContent = (template || '').replace('[name]', () => me.name).replace('[set]', () => me.vendor);

    list.replaceChildren(...items.map(buildCard));
    found.hidden = false;
    reveal(this.closest('.so-alt-wrap'));
    this.#reconcile(items);
  }

  /**
   * Makes the two rows agree: a product listed in the first row is not repeated in the second, the second row goes away when
   * the first has enough products, and when both stay only one of them keeps its "sold out here, but these are ready to
   * ship" sentence (the first; the second keeps its heading).
   * @param {Item[]} items the products of the first row
   */
  #reconcile(items) {
    const fallback = this.querySelector('[data-fallback]');
    if (!(fallback instanceof HTMLElement)) return;

    const shown = new Set(items.map((item) => handleOf(item.u)).filter(Boolean));
    for (const link of fallback.querySelectorAll('a.so-card')) {
      if (shown.has(handleOf(link.getAttribute('href')))) link.closest('li')?.remove();
    }
    const minimum = Math.max(1, parseInt(this.dataset.hideFallbackMin || '3', 10) || 3);
    if (items.length >= minimum || !fallback.querySelector('a.so-card')) fallback.hidden = true;

    const found = this.querySelector('[data-found]');
    const sub = fallback.querySelector('[data-fallback-sub]');
    if (sub instanceof HTMLElement && found instanceof HTMLElement && !found.hidden && !fallback.hidden) sub.hidden = true;
  }
}

/* ------------------------------------------------------------------ scoped recommendation rows */

/**
 * "More from this set" (same_set) and "Same character" (same_character), used by sections/product-recommendations.liquid.
 * The server renders the row hidden; it is shown only when there is at least one in-stock product to list. Both rows read the
 * same group request, so they cost one request together.
 */
class SoScopeRow extends HTMLElement {
  /** @type {(() => void) | null} */
  #cancel = null;

  connectedCallback() {
    if (this.hasAttribute('data-started')) return;
    this.setAttribute('data-started', '');
    this.#cancel = whenNear(this, () => {
      this.#run().catch(() => {});
    });
  }

  disconnectedCallback() {
    this.#cancel?.();
  }

  async #run() {
    const ds = this.dataset;
    const me = meFrom(ds);
    const source = ds.source || DEFAULT_SOURCE;
    const count = Math.max(1, parseInt(ds.count || '4', 10) || 4);
    const character = ds.scope === 'same_character';
    const items = character ? await this.#character(source, me, count) : await this.#set(source, me, count);

    if (!items.length) {
      if (ds.design === 'true') this.#reveal([], me, character);
      return;
    }
    this.#reveal(items.slice(0, count), me, character);
  }

  /** Other in-stock products of the same set and the same kind. @param {string} source @param {Me} me @param {number} need @returns {Promise<Item[]>} */
  async #set(source, me, need) {
    if (!usableSet(me.vendor) || (me.kind !== 'single' && me.kind !== 'sealed')) return [];
    const kept = await groupItems(source, me, {
      keep: (item) =>
        String(item.id) !== me.id && item.a === 1 && item.v === me.vendor && item.k === me.kind && (me.game === 'other' || item.g === me.game),
      own: `vendor:"${quoted(me.vendor)}"`,
      need,
    });
    return rank(me, rotate(kept, seedOf(me.id)));
  }

  /** Other in-stock cards of the same character, not the printings of this card. @param {string} source @param {Me} me @param {number} need @returns {Promise<Item[]>} */
  async #character(source, me, need) {
    if (!me.base || (me.game !== 'pokemon' && me.game !== 'onepiece') || (me.kind !== 'single' && me.kind !== 'graded')) return [];
    const needle = ` ${normalize(me.base)} `;
    const kept = await groupItems(source, me, {
      keep: (item) =>
        String(item.id) !== me.id &&
        item.a === 1 &&
        item.k === 'single' &&
        item.g === me.game &&
        ` ${normalize(cardName(item.t))} `.includes(needle) &&
        !sameCard(me, item),
      own: `title:"${quoted(me.base)}"`,
      need,
    });
    return rank(me, rotate(kept, seedOf(me.id)));
  }

  /** @param {Item[]} items @param {Me} me @param {boolean} character */
  #reveal(items, me, character) {
    const list = this.querySelector('[data-scope-list]');
    const heading = this.querySelector('[data-scope-heading]');
    const sub = this.querySelector('[data-scope-sub]');
    const note = this.querySelector('[data-scope-note]');
    if (!list) return;

    if (items.length) {
      const subtitle = character ? me.base : me.vendor;
      if (heading) {
        const template = this.dataset.heading || heading.textContent || '';
        heading.textContent = template.replace('[set]', () => me.vendor).replace('[name]', () => me.base || me.name);
      }
      list.replaceChildren(...items.map(buildCard));
      if (sub instanceof HTMLElement && subtitle) {
        sub.textContent = subtitle;
        sub.hidden = false;
      }
      if (note instanceof HTMLElement) note.hidden = true;
    } else if (note instanceof HTMLElement) {
      note.hidden = false;
    }

    reveal(this.closest('[data-so-wrap]'));
  }
}

if (!customElements.get('so-alternatives')) customElements.define('so-alternatives', SoAlternatives);
if (!customElements.get('so-scope-row')) customElements.define('so-scope-row', SoScopeRow);
